"use client";
import { yupResolver } from "@hookform/resolvers/yup";
import Link from "next/link";
import { useForm } from "react-hook-form";
import * as yup from "yup";
// internal
import { postData } from "@/api/api";
import { useRouter } from "next/navigation";
import { useState } from "react";
import ErrorMsg from "../common/error-msg";

type FormData = {
   name: string;
  phone: string;
  email?: string;
};

const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  phone: yup.string().required("Phone is required"),
  email: yup.string().email("Invalid email").optional(),
});

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800&display=swap');

  * { box-sizing: border-box; }

  .register-card {
    display: flex;
    flex-direction: row;
    border-radius: 28px;
    overflow: hidden;
    box-shadow: 0 32px 80px rgba(0,0,0,0.13);
    font-family: 'Sora', sans-serif;
    max-width: 940px;
    margin: 0 auto;
  }

  /* ── LEFT PANEL ── */
  .register-left {
    width: 320px;
    flex-shrink: 0;
    background: linear-gradient(150deg, #FFB347 0%, #FF7043 50%, #FF4B6E 100%);
    padding: 48px 38px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    position: relative;
    overflow: hidden;
  }

  .register-left::before {
    content: '';
    position: absolute;
    top: -80px; left: -80px;
    width: 300px; height: 300px;
    border-radius: 50%;
    background: rgba(255,255,255,0.12);
    pointer-events: none;
  }

  .register-left::after {
    content: '';
    position: absolute;
    bottom: -100px; right: -60px;
    width: 340px; height: 340px;
    border-radius: 50%;
    background: rgba(255,255,255,0.08);
    pointer-events: none;
  }

  .reg-blob {
    position: absolute;
    top: 40%; left: 30%;
    width: 180px; height: 180px;
    border-radius: 50%;
    background: rgba(255,255,255,0.06);
    pointer-events: none;
  }

  .reg-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    position: relative;
    z-index: 1;
  }

  .reg-brand-icon {
    width: 40px; height: 40px;
    background: rgba(255,255,255,0.25);
    border-radius: 12px;
    display: flex; align-items: center; justify-content: center;
    font-size: 18px;
    backdrop-filter: blur(4px);
  }

  .reg-brand-name {
    color: white;
    font-weight: 800;
    font-size: 14px;
    letter-spacing: 3px;
  }

  .reg-left-copy {
    position: relative;
    z-index: 1;
  }

  .reg-left-copy h2 {
    color: white;
    font-size: 28px;
    font-weight: 800;
    line-height: 1.2;
    margin-bottom: 14px;
    text-shadow: 0 2px 12px rgba(0,0,0,0.10);
  }

  .reg-left-copy p {
    color: rgba(255,255,255,0.82);
    font-size: 13px;
    line-height: 1.7;
    font-weight: 300;
  }

  .reg-features {
    list-style: none;
    padding: 0; margin: 16px 0 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    position: relative;
    z-index: 1;
  }

  .reg-features li {
    display: flex;
    align-items: center;
    gap: 8px;
    color: rgba(255,255,255,0.88);
    font-size: 12.5px;
    font-weight: 400;
  }

  .reg-features li::before {
    content: '✓';
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px; height: 18px;
    border-radius: 50%;
    background: rgba(255,255,255,0.25);
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .reg-ai-badge {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    background: rgba(255,255,255,0.18);
    border: 1px solid rgba(255,255,255,0.30);
    border-radius: 24px;
    padding: 8px 16px;
    color: white;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.5px;
    position: relative;
    z-index: 1;
    backdrop-filter: blur(6px);
  }

  .reg-ai-badge .star {
    display: inline-block;
    animation: reg-spin 4s linear infinite;
    font-size: 13px;
  }

  @keyframes reg-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* ── RIGHT PANEL ── */
  .register-right {
    flex: 1;
    background: white;
    padding: 44px 48px;
    overflow-y: auto;
  }

  .register-right h3 {
    font-size: 24px;
    font-weight: 800;
    color: #111;
    margin-bottom: 4px;
    font-family: 'Sora', sans-serif;
  }

  .register-right .reg-subtitle {
    color: #aaa;
    font-size: 13px;
    margin-bottom: 30px;
    font-family: 'Sora', sans-serif;
    font-weight: 400;
  }

  .reg-form-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18px;
  }

  .reg-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  .reg-field.full-width {
    grid-column: 1 / -1;
  }

  .reg-field label {
    font-size: 10.5px;
    font-weight: 700;
    color: #444;
    letter-spacing: 0.8px;
    text-transform: uppercase;
    font-family: 'Sora', sans-serif;
  }

  .reg-field label span {
    color: #FF4B6E;
    margin-left: 2px;
  }

  .reg-input-wrap {
    position: relative;
  }

  .reg-input-icon {
    position: absolute;
    left: 13px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 15px;
    pointer-events: none;
    opacity: 0.45;
  }

  .reg-field input[type="text"],
  .reg-field input[type="email"] {
    border: 1.5px solid #ebebeb;
    border-radius: 12px;
    padding: 12px 13px 12px 38px;
    font-size: 13.5px;
    font-family: 'Sora', sans-serif;
    color: #111;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    background: #f8f8f8;
    width: 100%;
  }

  .reg-field input[type="text"]:focus,
  .reg-field input[type="email"]:focus {
    border-color: #FF7043;
    box-shadow: 0 0 0 3px rgba(255,112,67,0.12);
    background: #fff;
  }

  .reg-field input::placeholder {
    color: #c0c0c0;
    font-weight: 300;
  }

  .reg-checkbox-row {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 9px;
    margin-top: 2px;
  }

  .reg-checkbox-row input[type="checkbox"] {
    accent-color: #FF7043;
    width: 16px;
    height: 16px;
    cursor: pointer;
    flex-shrink: 0;
  }

  .reg-checkbox-row label {
    font-size: 13px;
    color: #666;
    font-family: 'Sora', sans-serif;
    cursor: pointer;
    font-weight: 400;
  }

  .reg-btn-primary {
    grid-column: 1 / -1;
    background: linear-gradient(135deg, #FFB347 0%, #FF7043 50%, #FF4B6E 100%);
    color: white;
    border: none;
    border-radius: 14px;
    padding: 14px;
    font-size: 15px;
    font-weight: 700;
    font-family: 'Sora', sans-serif;
    cursor: pointer;
    letter-spacing: 0.3px;
    transition: opacity 0.2s, transform 0.15s, box-shadow 0.2s;
    width: 100%;
    box-shadow: 0 6px 20px rgba(255,112,67,0.30);
    margin-top: 2px;
  }

  .reg-btn-primary:hover:not(:disabled) {
    opacity: 0.92;
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(255,112,67,0.38);
  }

  .reg-btn-primary:active:not(:disabled) {
    transform: translateY(0);
  }

  .reg-btn-primary:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }

  .reg-toast {
    grid-column: 1 / -1;
    border-radius: 12px;
    padding: 11px 16px;
    font-size: 13px;
    text-align: center;
    font-family: 'Sora', sans-serif;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
    color: #166534;
    font-weight: 500;
  }

  .reg-divider {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    gap: 12px;
    color: #ddd;
    font-size: 12px;
    font-family: 'Sora', sans-serif;
  }

  .reg-divider::before, .reg-divider::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #f0f0f0;
  }

  .reg-btn-secondary {
    grid-column: 1 / -1;
    background: #111;
    color: white;
    border: none;
    border-radius: 14px;
    padding: 14px;
    font-size: 15px;
    font-weight: 700;
    font-family: 'Sora', sans-serif;
    cursor: pointer;
    letter-spacing: 0.3px;
    transition: opacity 0.2s, transform 0.15s;
    text-align: center;
    display: block;
    text-decoration: none;
    width: 100%;
  }

  .reg-btn-secondary:hover {
    opacity: 0.82;
    transform: translateY(-1px);
  }

  /* ── MOBILE: app-style curved banner ── */
  @media (max-width: 700px) {
    .register-card {
      flex-direction: column;
      border-radius: 0;
      box-shadow: none;
      background: transparent;
    }

    .register-left {
      width: 100%;
      padding: 36px 24px 52px;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      min-height: unset;
      border-radius: 0 0 36px 36px;
      box-shadow: 0 8px 32px rgba(255, 112, 67, 0.28);
      position: relative;
    }

    .register-left::before {
      display: block;
      top: -40px; left: -40px;
      width: 160px; height: 160px;
    }

    .register-left::after {
      display: block;
      bottom: -30px; right: -20px;
      width: 180px; height: 180px;
    }

    .reg-blob { display: none; }

    .reg-brand {
      flex-direction: row;
      align-items: center;
    }

    .reg-left-copy {
      flex: unset;
    }

    .reg-left-copy h2 {
      font-size: 22px;
      margin-bottom: 4px;
      margin-top: 10px;
    }

    .reg-left-copy p {
      display: block;
      font-size: 12px;
    }

    .reg-features { display: none; }

    .reg-ai-badge {
      display: inline-flex;
      margin-top: 12px;
    }

    .register-right {
      padding: 32px 20px 28px;
      border-radius: 0;
      box-shadow: none;
    }

    .reg-form-grid {
      grid-template-columns: 1fr;
    }

    .reg-field.full-width { grid-column: 1; }
    .reg-checkbox-row { grid-column: 1; }
    .reg-btn-primary { grid-column: 1; }
    .reg-toast { grid-column: 1; }
    .reg-divider { grid-column: 1; }
    .reg-btn-secondary { grid-column: 1; }
  }
`;

const RegisterForm = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });
  const router = useRouter();
  const [loading, setLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);

    try {
     const createAccount = await postData(`/customer/auth/Customerregister`, {
  name: data?.name,
  phone: data?.phone,
  email: data?.email || null,
  image: "https://cdn-icons-png.flaticon.com/512/9368/9368192.png",
});

      if (!createAccount?.success) {
        setToastMessage(
          (createAccount?.message as string) ||
            "Something went wrong. Try again."
        );
        console.log(createAccount?.message);
        return;
      }

      setToastMessage(createAccount?.message as string);
      console.log({ createAccount });
      router.push("/login");
    } catch (error) {
      setToastMessage((error as string) || "Internal Server Error");
      console.log(error as string);
    } finally {
      setLoading(false);
    }
  });

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: styles }} />

      <div className="register-card">
        {/* ── Left Panel ── */}
        <div className="register-left">
          <div className="reg-brand">
            <div className="reg-brand-icon">🛒</div>
            <span className="reg-brand-name">SHOPNOW</span>
          </div>

          <div className="reg-left-copy">
            <h2>Hello,<br/>Welcome!</h2>
            <p>Create your account and start your personalized shopping journey today.</p>
            <ul className="reg-features">
              <li>Free delivery on first order</li>
              <li>AI-powered recommendations</li>
              <li>Secure & fast checkout</li>
            </ul>
          </div>

          <div className="reg-ai-badge">
            <span className="star">✦</span> iMall-Genuine Products with Express Delivery

          </div>

          <div className="reg-blob" />
        </div>

        {/* ── Right Panel ── */}
        <div className="register-right">
          <h3>Create an Account</h3>
          <p className="reg-subtitle">Fill in your details to get started</p>

        <form onSubmit={onSubmit}>
  <div className="reg-form-grid">

    {/* Full Name */}
    <div className="reg-field full-width">
      <label htmlFor="name">Full Name <span>*</span></label>
      <div className="reg-input-wrap">
        <span className="reg-input-icon">👤</span>
        <input
          id="name"
          {...register("name")}
          type="text"
          placeholder="Enter your full name..."
        />
      </div>
      <ErrorMsg msg={errors.name?.message!} />
    </div>

    {/* Phone */}
    <div className="reg-field full-width">
      <label htmlFor="phone">Phone Number <span>*</span></label>
      <div className="reg-input-wrap">
        <span className="reg-input-icon">📞</span>
        <input
          id="phone"
          {...register("phone")}
          type="text"
          placeholder="+880 1XXX XXXXXX"
        />
      </div>
      <ErrorMsg msg={errors.phone?.message!} />
    </div>

    {/* Email (Optional) */}
    <div className="reg-field full-width">
      <label htmlFor="email">Email Address</label>
      <div className="reg-input-wrap">
        <span className="reg-input-icon">✉️</span>
        <input
          id="email"
          {...register("email")}
          type="email"
          placeholder="you@example.com (optional)"
        />
      </div>
      <ErrorMsg msg={errors.email?.message!} />
    </div>

    {/* Submit */}
    <button className="reg-btn-primary" type="submit" disabled={loading}>
      {loading ? "Please wait..." : "Register Now"}
    </button>

    {/* Toast */}
    {toastMessage && (
      <div className="reg-toast">{toastMessage}</div>
    )}

    {/* Divider */}
    <div className="reg-divider"><span>or</span></div>

    {/* Login */}
    <Link href="/login" className="reg-btn-secondary">
      Login Now
    </Link>

  </div>
</form>
        </div>
      </div>
    </>
  );
};

export default RegisterForm;